import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit } from '@angular/core';
import { TariffOptionSap, TariffSelectionCardData, TextNodeType } from '@mwe/models';
import { isAllesSicherOptionAlreadyApplied, isArrayWithMinOneItem } from '@mwe/utils';
import { HeadlineService } from '../../headline/headline.service';
import { TariffSelectionStore } from '../services/tariff-selection.store';
import { PriceSheetService, ProfileService } from '@mwe/services';
import { HeadlineComponent } from '../../headline/headline.component';
import { TariffSelectionCardStoreComponent } from '../tariff-selection-card/tariff-selection-card-store.component';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { TariffsSelectionButtonComponent } from '../tariffs-selection-button/tariffs-selection-button.component';
import { ExpanderComponent } from '../../expander/expander.component';
import { TariffCardAdditionalOptionsComponent } from '../tariff-card-additional-options/tariff-card-additional-options.component';
import { TariffCardRouterComponent } from '../tariff-card-router/tariff-card-router.component';
import { InfoboxComponent } from '../../infobox/infobox.component';
import { ProductIconComponent } from '../../product-icon/product-icon.component';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { FeatureToggleEnum, ServiceStateEnum } from '@mwe/constants';
import { CorrelatedCompanyReferencePipe } from '@mwe/ui/pipes';
import { TariffCardAdditionalOptionsSelectionComponent } from '../tariff-card-additional-options-selection/tariff-card-additional-options-selection.component';

@Component({
  selector: 'mwe-tariff-selection-container',
  templateUrl: './tariff-selection-container.component.html',
  providers: [HeadlineService],
  imports: [
    HeadlineComponent,
    TariffSelectionCardStoreComponent,
    TranslateDirective,
    TariffsSelectionButtonComponent,
    ExpanderComponent,
    TariffCardAdditionalOptionsComponent,
    TariffCardRouterComponent,
    TranslatePipe,
    InfoboxComponent,
    ProductIconComponent,
    NgTemplateOutlet,
    CorrelatedCompanyReferencePipe,
    AsyncPipe,
    TariffCardAdditionalOptionsSelectionComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TariffSelectionContainerComponent implements OnInit {
  protected readonly headlineService = inject(HeadlineService);
  protected readonly priceSheetService = inject(PriceSheetService);
  private readonly profileService = inject(ProfileService);
  private readonly store = inject(TariffSelectionStore);

  readonly isReadOnly = input<boolean>(false);
  readonly hideAdditionalOptions = input<boolean>(false);
  readonly headlineReference = input<TextNodeType>('h3');
  readonly selectedTariffs = input.required<TariffSelectionCardData[]>(); // selectedTariffs comes from signal() do not manipulate it!!!! use TariffSelectionStore!!!!!
  readonly cardCategoryTitleKeyPrefix = input<string>('tariff.selectionCard.stromGas');
  readonly allesSicherTextParams = input<{ url: string }>(undefined);
  readonly isKundeWerden = input<boolean>(false);
  readonly isRelocation = input<boolean>(false);
  readonly isTariffChange = input<boolean>(false);
  readonly isInternet = input<boolean>(false);
  readonly hasNewVoucher = input<boolean>(false);
  readonly showTariffTypeFilter = input<boolean>(false);
  readonly tariffSheetUrls = input<Record<string, string>>();
  readonly reloadHandler = input<(tariff: TariffSelectionCardData) => void>();

  protected readonly ServiceStateEnum = ServiceStateEnum;

  readonly actionLinkLabel = computed(() => {
    if (this.isInternet() && this.profileService.isFeatureToggleEnabled(FeatureToggleEnum.INTERNET_V2_ENABLED)) {
      return 'tariff.selectionCard.mvnoTariffPdfLink';
    } else {
      return 'tariff.selectionCard.tariffPdfLink';
    }
  });

  ngOnInit(): void {
    this.headlineService.setReferenceHeadline(this.headlineReference());
    if (this.isTariffChange()) this.store.updateProcessType('Tarifwechsel');
  }

  getStaticTariffSheetUrl(key: string): string | undefined {
    return key ? this.tariffSheetUrls()?.[key] : undefined;
  }

  shouldShowLegalInfobox(tariff: TariffSelectionCardData) {
    if (this.isInternet()) {
      return !!tariff.priceIndication?.rechtsText;
    }
    return !!tariff.priceIndication?.rechtsText && !this.isReadOnly();
  }

  checkIfAdditionalCardIsVisible(item?: TariffSelectionCardData): boolean {
    const allesSicherForTariffChangeDisabled =
      !this.isInternet() &&
      !this.isKundeWerden() &&
      !this.isRelocation() &&
      !this.profileService.isFeatureToggleEnabled(FeatureToggleEnum.TARIFF_CHANGE_ALLES_SICHER_ENABLED);

    const additionalOptionsDisabledForKW =
      this.isKundeWerden() &&
      !this.isInternet() &&
      !this.isRelocation() &&
      !this.profileService.isFeatureToggleEnabled(FeatureToggleEnum.NEW_CLIENT_ADDITIONAL_OPTIONS_ENABLED);

    const additionalOptionsForRelocationDisabled =
      !this.isInternet() &&
      !this.isKundeWerden() &&
      this.isRelocation() &&
      !this.profileService.isFeatureToggleEnabled(FeatureToggleEnum.RELOCATION_ADDITIONAL_OPTIONS_ENABLED);

    if (
      this.hideAdditionalOptions() ||
      allesSicherForTariffChangeDisabled ||
      additionalOptionsDisabledForKW ||
      additionalOptionsForRelocationDisabled
    ) {
      return false;
    }

    if (this.isReadOnly()) {
      // user can only store one option of group. not default -> it is selected
      const options = this.getAdditionalOptions(item.userSelection);

      return (
        (options?.length && !options?.[0]?.default) ||
        isAllesSicherOptionAlreadyApplied(item.userSelection, this.store.additionalOptionGroupName())
      );
    }

    return this.hasAdditionalOptions(item.tariffOptionsSap);
  }

  showAllesSicherAlreadyAppliedInfoText(tariffOptions: TariffOptionSap[]): boolean {
    return isAllesSicherOptionAlreadyApplied(tariffOptions, this.store.additionalOptionGroupName());
  }

  checkIfRouterCardIsVisible(item?: TariffSelectionCardData): boolean {
    if (!item) {
      return false;
    }
    const category = item.category?.toLowerCase();
    const isMobileInternet = category === 'mobiles-internet' || category === 'mobiles internet';
    const hasRouterData = !!item.priceIndication?.internet?.router?.name;

    return isMobileInternet && hasRouterData;
  }

  private hasAdditionalOptions(options: TariffOptionSap[]): boolean {
    return isArrayWithMinOneItem(this.getAdditionalOptions(options));
  }

  private getAdditionalOptions(options: TariffOptionSap[]): TariffOptionSap[] {
    return options?.filter(t => t?.art?.toLowerCase() === this.store?.additionalOptionGroupName()?.toLowerCase());
  }
}
